import React, { useState, useEffect, useRef, MutableRefObject } from 'react';
import Helmet from 'react-helmet';
import 'src/common/style/common.scss';
import './SevOneBenefitsCalc.scss';

/* Hooks & Utilities */
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useCookiesURL } from '../../../common/hooks/widgets/useCookiesURL';

import { useLang, LangDefaults } from 'src/common/hooks/widgets/useLang';
import { useSevOneBenefitsCalcSelector } from '../lib/redux/hooks';
import { createUseTranslation } from '../../../common/hooks/widgets/useTranslation';
import { calculateSavings } from '../hooks/calculateSavings';

/* Constants & Config */
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { UT30SevOneBenefitsCalc } from '../constants/default';
import * as URX_FORM from '../constants/urx-form';
import Cookies from 'js-cookie';
import messages from '../locales/messages';

/* URX Form & Segement Includes */
import { segmentsTracking } from 'src/common/lib/tracker';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from './Thanks/Thanks';

/* Carbon Components */
import {
  NumberInput,
  ListItem,
  UnorderedList,
  ButtonSet,
  Button,
} from 'carbon-components-react';
import { CheckmarkFilled20, ArrowRight20 } from '@carbon/icons-react';

/* External Scripts */
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { shortcode } from '../SevOneBenefitsCalculator.widget';

export interface AppProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
  overridePercentage: string;
}

const handleInputChange = (name: any) => {
  segmentsTracking('CTA Clicked', {
    pageTitle: 'SevOne ROI Calculator',
    object: 'SevOne ROI Calculator Page',
    resultValue: name + ' input changed',
    CTA: name,
    location: 'SevOne ROI Calculator Widget',
    action: '',
    field: '',
  });
};

const SevOneBenefitsCalc = ({ langCode, overridePercentage }: AppProps) => {
  /* State & Refs */
  const [downtime, setDowntime] = useState(60);
  const [costPerHour, setCostPerHour] = useState(25000);
  const [sevOneDiscount, setSevOneDiscount] = useState(0.1);
  const [sevOneSavingsOutput, setSevOneSavingsOutput] = useState(0);
  const resizeTimeoutRef = useRef<number | null>(null); // Explicitly typing the ref
  // const downtimeRef = useRef<HTMLInputElement>(null);
  const downtimeRef = useRef() as MutableRefObject<HTMLInputElement>;
  // const costPerHourRef = useRef<HTMLInputElement>(null);
  const costPerHourRef = useRef() as MutableRefObject<HTMLInputElement>;
  const sevOneSavings = useRef<HTMLHeadingElement>(null);
  const cookieURL = useCookiesURL();
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const [closeModal, setCloseModal] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formID, setFormID] = useState('');

  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );
  const { bpName, companyId, logo, desc, mssp } = useBPName(
    UT30SevOneBenefitsCalc,
    true,
  );

  const AEMMessages = useSevOneBenefitsCalcSelector(
    (state) => state.translations.messages,
  );
  const useTranslation = createUseTranslation(messages, AEMMessages);

  const equalizeHeights = (elements: any[]) => {
    if (!elements || elements.length === 0) return;
    let tallestHeight = 0;

    // Reset heights to auto before recalculating (to account for responsive changes)
    elements.forEach((element) => {
      element.style.height = 'auto';
    });

    // Determine the tallest element
    elements.forEach((element) => {
      const elementHeight = element.offsetHeight;
      if (elementHeight > tallestHeight) {
        tallestHeight = elementHeight;
      }
    });

    // Set all elements to the tallest height
    elements.forEach((element) => {
      element.style.height = `${tallestHeight}px`;
    });
  };

  useEffect(() => {
    const statiticHeadings = document.querySelectorAll(
      '.sobc-calculation-section',
    );
    equalizeHeights(Array.from(statiticHeadings)); // Run the equalizeHeights function initially

    // Add resize event listener
    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Set a new timeout
      resizeTimeoutRef.current = window.setTimeout(() => {
        equalizeHeights(Array.from(statiticHeadings));
      }, 200); // Delay of 200ms
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccessMultiple =
    window.onUrxFormSubmitSuccessMultiple || {};
  window.onUrxFormSubmitSuccessMultiple[URX_FORM.INSTANCE_ID] = function (
    param,
  ) {
    setFormComplete(!formComplete);
    setDrawerOpen(false);
    setCloseModal(true);
  };

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: formID,
        dcq: {
          Q_NOTESDATA: `${cookieURL} | FormId: ${formID} | Average Unplanned Downtime Hours: ${downtime} | Average Cose Per Hour: ${costPerHour}`,
          Q_ASSET_NAME: '',
        },
      }),
    );
  };

  /* Effects */
  useEffect(() => {
    if (overridePercentage) {
      setSevOneDiscount(Number(overridePercentage) / 100);
    }
  }, [overridePercentage]);

  useEffect(() => {
    calculateSavings(
      downtime,
      costPerHour,
      sevOneDiscount,
      setSevOneSavingsOutput,
    );
  }, [downtime, costPerHour, sevOneDiscount]);

  useEffect(() => {
    setCookie();
  }, [cookieURL]);

  /* Handlers */

  return (
    <div
      id="sevonebenefitscalc_widget"
      className="sevonebenefitscalc_widget widget-styles"
    >
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      <p className="sobc-widget_intro_text">
        {useTranslation('widgetHeading')} {Number(sevOneDiscount * 100)}%
      </p>
      <div className="bx--grid bx--grid--condensed">
        <div className="bx--row">
          <div className="x-bx--col bx--col-sm-4 bx--col-md-4 bx--col-lg-8 bx--col-xlg-4 x-bx--col-max-5">
            <div className="sobc-calculation-inputs">
              <NumberInput
                label={useTranslation('downtimeLabel')}
                helperText={useTranslation('downtimeHelper')}
                id="downtime"
                size="md"
                min={0}
                step={5}
                ref={downtimeRef}
                className="number-input calc-input"
                value={downtime}
                onChange={() => {
                  const value = downtimeRef.current?.value;
                  handleInputChange(downtimeRef.current.id);
                  if (value !== undefined) {
                    setDowntime(Number(value));
                  }
                  setCookie();
                }}
              />
              <NumberInput
                label={useTranslation('costPerHourLabel')}
                helperText={useTranslation('costPerHourHelper')}
                id="costPerHour"
                size="md"
                min={0}
                step={500}
                ref={costPerHourRef}
                className="number-input calc-input"
                value={costPerHour}
                onChange={() => {
                  const value = costPerHourRef.current?.value;
                  handleInputChange(costPerHourRef.current.id);
                  if (value !== undefined) {
                    setCostPerHour(Number(value));
                  }
                  setCookie();
                }}
              />
            </div>
          </div>
          <div className="x-bx--col bx--col-sm-4 bx--col-md-4 bx--col-lg-8 bx--col-xlg-4 x-bx--col-max-5">
            <div className="sobc-calculation-section sobc-left-side">
              <p className="sobc-result-heading">
                {useTranslation('resultHeading')}
              </p>
              <h3
                className="sobc-widget-savings"
                id="sevOneSavings"
                ref={sevOneSavings}
              >
                {sevOneSavingsOutput
                  .toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })
                  .replace(/\.\d{2}$/, '')}
              </h3>
              <p className="sobc-result-disclaimer">
                {useTranslation('resultDisclaimer')}
              </p>
              <ButtonSet>
                <Button
                  id="trial_btn"
                  kind="primary"
                  className="full-width-btn"
                  renderIcon={ArrowRight20}
                  onClick={() => {
                    segmentsTracking('CTA Clicked', {
                      pageTitle: 'SevOne Benefits Calculator',
                      object: 'SevOne Benefits Calculator Page',
                      resultValue: 'CTA clicked',
                      CTA: 'Start Trial',
                      location: 'SevOne Benefits Calculator',
                      action: '',
                      field: '',
                    });
                    initUrxForm(
                      environment,
                      URX_FORM.INSTANCE_ID,
                      URX_FORM.FORM_ID,
                      urxlang as IBMLocale,
                      companyId,
                      shortcode,
                      setFormID,
                    );
                    setDrawerOpen(true);
                    setCookie();
                  }}
                >
                  {useTranslation('primary_cta_text')}
                </Button>
              </ButtonSet>
            </div>
          </div>
          <div className="x-bx--col bx--col-sm-4 bx--offset-md-4 bx--col-md-4 bx--offset-lg-6 bx--col-lg-8 bx--offset-xlg-0 bx--col-xlg-4 x-bx--col-max-5">
            <div className="sobc-calculation-section sobc-right-side">
              <p className="sobc-additional-benefits">
                {useTranslation('benefitsHeading')}
              </p>
              <UnorderedList className="sobc-features-list">
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits1')}
                </ListItem>
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits2')}
                </ListItem>
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits3')}
                </ListItem>
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits4')}
                </ListItem>
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits5')}
                </ListItem>
                <ListItem>
                  <CheckmarkFilled20 /> {useTranslation('benefits6')}
                </ListItem>
              </UnorderedList>
            </div>
          </div>
        </div>
      </div>
      <div className="bx--grid bx--grid--condensed sobc-footer-disclimer">
        <div className="bx--row">
          <div className="bx--col bx--col-sm-8 bx--col-md-16 bx--col-lg-15 bx--col-xlg-10 bx--col-max-10">
            <p className="sobc-widget-disclaimer">
              {useTranslation('widgetDisclaimer')}
            </p>
          </div>
        </div>
      </div>
      <div>
        <FormModal
          pageTitle="SevOne ROI Calculator"
          object="SevOne ROI Calculator Page"
          productTitle={'SevOne ROI Calculator'}
          pageUrl={''}
          location={'File transfer'}
          bpName={bpName}
          logo={logo}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          formId={formID}
          id={URX_FORM.INSTANCE_ID}
          closeModal={closeModal}
          setCloseModal={setCloseModal}
        ></FormModal>
        {formComplete && (
          <Thanks
            downtime={downtime}
            costPerHour={costPerHour}
            bpName={bpName}
          />
        )}
      </div>
    </div>
  );
};

export default SevOneBenefitsCalc;
